/**
 * 项目卡片骨架屏组件
 * 在数据加载时显示占位内容
 * 设计理念: 与项目视图保持一致的圆角和阴影风格
 */
import React from 'react';
import { Card, Skeleton, Row, Col } from 'antd';

// 统一配色系统
const SKELETON_THEME = {
  background: 'rgba(224, 230, 234, 0.86)',
  border: '1px solid rgba(16, 27, 35, 0.08)',
  shadow: '0 18px 40px rgba(46, 60, 70, 0.12)',
  borderRadius: 22,
};

const cardShellStyle = {
  background: SKELETON_THEME.background,
  border: SKELETON_THEME.border,
  borderRadius: SKELETON_THEME.borderRadius,
  boxShadow: SKELETON_THEME.shadow
};

const CompactCardSkeleton: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => (
  <Card
    size="small"
    style={cardShellStyle}
    bodyStyle={{ padding: isMobile ? '12px 14px' : '14px 16px' }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
      <Skeleton.Input active size="small" style={{ width: 100 }} />
      <Skeleton.Button active size="small" style={{ width: 56 }} />
    </div>
    <Skeleton.Input active size="small" style={{ width: '75%', marginBottom: 8 }} />
    <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 12 }} />
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Skeleton.Input active size="small" style={{ width: 68 }} />
      <Skeleton.Input active size="small" style={{ width: 52 }} />
    </div>
  </Card>
);

const FullCardSkeleton: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => (
  <Card
    size={isMobile ? 'small' : 'default'}
    style={cardShellStyle}
    bodyStyle={{ padding: isMobile ? 14 : 24 }}
  >
    {/* 头部区域 */}
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <Skeleton.Input active size="small" style={{ width: 110 }} />
        <Skeleton.Button active size="small" style={{ width: 62 }} />
      </div>
      <Skeleton.Input active size="small" style={{ width: '60%', marginBottom: 6 }} />
      <Skeleton.Input active size="small" style={{ width: '40%' }} />
    </div>

    {/* 预算燃耗区块 */}
    <div style={{ marginBottom: 18 }}>
      <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 10 }} />
      <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 12 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Skeleton.Input active size="small" style={{ width: 80 }} />
        <Skeleton.Input active size="small" style={{ width: 80 }} />
      </div>
    </div>

    {/* 进度完成度区块 */}
    <div style={{ marginBottom: 18 }}>
      <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: '100%' }} />
    </div>

    {/* 底部统计区块 */}
    <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
      <Col span={8}>
        <Skeleton.Input active size="small" style={{ width: '100%' }} />
      </Col>
      <Col span={8}>
        <Skeleton.Input active size="small" style={{ width: '100%' }} />
      </Col>
      <Col span={8}>
        <Skeleton.Input active size="small" style={{ width: '100%' }} />
      </Col>
    </Row>

    {/* 操作按钮 */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Skeleton.Button active size="small" style={{ width: 80 }} />
      <div>
        <Skeleton.Button active size="small" style={{ width: 48, marginRight: 8 }} />
        <Skeleton.Button active size="small" style={{ width: 68 }} />
      </div>
    </div>
  </Card>
);

const ListItemSkeleton: React.FC = () => (
  <Card size="small" style={{ ...cardShellStyle, marginBottom: 10, borderRadius: 18 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
      <div style={{ flex: 1 }}>
        <Skeleton.Input active size="small" style={{ width: 80, marginBottom: 4 }} />
        <Skeleton.Input active size="small" style={{ width: '70%' }} />
      </div>
      <Skeleton.Button active size="small" style={{ width: 54 }} />
    </div>
    <Skeleton.Input active size="small" style={{ width: '100%' }} />
  </Card>
);

const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div
    style={{
      background: SKELETON_THEME.background,
      border: SKELETON_THEME.border,
      borderRadius: 24,
      overflow: 'hidden',
      boxShadow: SKELETON_THEME.shadow,
      padding: 10
    }}
  >
    {/* 表头 */}
    <div
      style={{
        display: 'flex',
        padding: '12px 16px',
        background: 'rgba(210, 218, 224, 0.9)',
        borderBottom: '1px solid rgba(16, 27, 35, 0.08)',
        gap: 16
      }}
    >
      {[160, 100, 200, 140, 100, 160, 140].map((width, index) => (
        <Skeleton.Input key={index} active size="small" style={{ width }} />
      ))}
    </div>
    {/* 数据行 */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        style={{
          display: 'flex',
          padding: '14px 16px',
          borderBottom: '1px solid rgba(16, 27, 35, 0.06)',
          gap: 16
        }}
      >
        {[160, 100, 200, 140, 100, 160, 140].map((width, colIndex) => (
          <Skeleton.Input key={colIndex} active size="small" style={{ width }} />
        ))}
      </div>
    ))}
  </div>
);

export interface ProjectSkeletonProps {
  count?: number;
  isMobile?: boolean;
  viewMode?: 'card' | 'compact' | 'table' | 'list';
}

export const ProjectSkeleton: React.FC<ProjectSkeletonProps> = ({
  count = 6,
  isMobile = false,
  viewMode = 'card'
}) => {
  if (viewMode === 'table') {
    return <TableSkeleton rows={count} />;
  }

  if (viewMode === 'list') {
    return (
      <div>
        {Array.from({ length: count }).map((_, index) => (
          <ListItemSkeleton key={index} />
        ))}
      </div>
    );
  }

  const CardComponent = viewMode === 'compact' ? CompactCardSkeleton : FullCardSkeleton;
  const colConfig = viewMode === 'compact'
    ? { xs: 12, sm: 8, md: 6, lg: 6, xl: 4 }
    : { xs: 24, sm: 24, md: 12, lg: 8 };

  return (
    <Row gutter={[16, 16]}>
      {Array.from({ length: count }).map((_, index) => (
        <Col key={index} {...colConfig}>
          <CardComponent isMobile={isMobile} />
        </Col>
      ))}
    </Row>
  );
};

export default ProjectSkeleton;